/**
 * Cube filter operators for page-level card filters.
 * Mirrors AppSync `CubeFilterOperator`.
 */
export enum CubeFilterOperator {
	Equals = 'equals',
	NotEquals = 'notEquals',
	Contains = 'contains',
	Gt = 'gt',
	Gte = 'gte',
	Lt = 'lt',
	Lte = 'lte',
}

/** Runtime payload for date-range filter cards (WebSocket / cardData). */
export interface DateRangeFilterCardData {
	timeDimension?: {
		dimension: string;
		granularity: string;
		timeDimensionSource: string;
		dateRange: [string, string];
	};
	filters?: unknown[];
	metrics?: unknown[];
	dimensions?: unknown[];
	order?: Record<string, string>;
}

export type FilterCardRuntimeData =
	| DimensionFilterCardData
	| DateRangeFilterCardData;

/** Runtime payload for dropdown filter cards (WebSocket / cardData). */
export interface DimensionFilterCardData {
	controlType: 'dropdown';
	dimension: {
		member: string;
		label: string;
	};
	options: Array<{ value: string; label: string }>;
	selected: string[];
}

/** Page filter item serialized into `getItem.pageData` `pageFilters` AWSJSON. */
export interface PageCardFilterInput {
	cardId: string;
	member: string;
	operator?: CubeFilterOperator;
	values: string[];
	dataSource?: string;
}

/** In-memory selection tracked by {@link PageFilterService}. */
export interface PageCardFilterSelection {
	cardId: string;
	member: string;
	operator: CubeFilterOperator;
	values: string[];
	dataSource?: string;
	/** All option values at selection time (for all-selected = omit). */
	allOptionValues: string[];
}

export function isDimensionFilterCardData(
	value: unknown
): value is DimensionFilterCardData {
	if (!value || typeof value !== 'object') {
		return false;
	}
	const record = value as Record<string, unknown>;
	return (
		record['controlType'] === 'dropdown' &&
		typeof record['dimension'] === 'object' &&
		record['dimension'] !== null &&
		Array.isArray(record['options'])
	);
}

/**
 * Whether a partial selection should be sent in `pageFilters`.
 * Empty or all-options-selected means no filter (omit from query).
 */
export function shouldPublishPageFilter(
	selected: string[],
	allOptionValues: string[]
): boolean {
	if (selected.length === 0) {
		return false;
	}
	if (
		allOptionValues.length > 0 &&
		selected.length >= allOptionValues.length
	) {
		const allSet = new Set(allOptionValues);
		return !selected.every(v => allSet.has(v));
	}
	return true;
}
